/**
 * Caret navigation offsets for the shape-text engine: word steps, visual-row boundaries and
 * vertical row steps, all in the flat `paragraphsToPlainText` character space. It also answers
 * *ranges* — the word/row/paragraph spans a multi-click selects (WMPCM-2076) — which live here so
 * that "what counts as a word" stays single-sourced with the caret step that uses the same rule.
 *
 * Extracted as pure functions because cross-frame selection (WMPCM-9200) has to resolve the same
 * moves in a frame that is NOT the one holding keyboard focus. `ShapeManager` reaches a sibling
 * through its registered `ShapeTextFrameHandle`, and the handle needs somewhere convention-free to
 * delegate to — the alternative was duplicating row math per call site.
 *
 * Row identity comes from `ShapeTextCaret`, so a "row" here is a laid-out row of the real shape
 * outline (already narrowed by wrap bands), not a `TextLine` and not a paragraph.
 */
import type { ShapeTextCaret } from '@/lib/shapes/types/shape-text-caret'
import type { ShortcutPlatform } from '@/lib/keyboard/types/shortcut-platform'
import type { ChainSelectionExtendUnit } from './types/chain-selection-extend-unit'
import type { ShapeTextRange } from './types/shape-text-range'
import type { ShiftNavigationEvent } from './types/shift-navigation-event'

/**
 * A slot-boundary caret stores `x`/`yTop` on the slot it *ends* and `nextSlotX`/`nextSlotYTop` on
 * the slot the flow continues into. For navigation the continuation is the right answer — it is
 * where the next typed character lands, and it is what `activeCaret` draws the bar at
 * (`use-shape-text-selection.ts`). Using the raw `yTop` instead would put Home/End on the row above
 * whenever the caret sat exactly on a wrap.
 */
function rowTopOf(caret: ShapeTextCaret): number {
  return caret.nextSlotYTop ?? caret.yTop
}

function xOf(caret: ShapeTextCaret): number {
  return caret.nextSlotX ?? caret.x
}

function clampOffset(carets: ShapeTextCaret[], offset: number): number {
  return Math.max(0, Math.min(offset, carets.length - 1))
}

/** Distinct row tops in ascending visual order. */
function rowTops(carets: ShapeTextCaret[]): number[] {
  const seen = new Set<number>()

  for (const caret of carets) seen.add(rowTopOf(caret))

  return [...seen].sort((a, b) => a - b)
}

/** The offset in `row` whose x is nearest `x`; null when the row holds no carets. */
function offsetNearestXInRow(carets: ShapeTextCaret[], row: number, x: number): number | null {
  let best: number | null = null
  let bestDelta = Number.POSITIVE_INFINITY

  for (let i = 0; i < carets.length; i++) {
    const caret = carets[i]!

    if (rowTopOf(caret) !== row) continue

    const delta = Math.abs(xOf(caret) - x)

    // `<` not `<=` keeps the FIRST of several equally-close carets, which matters at a row's
    // trailing edge where the row-end caret and the next row's start caret can share an x.
    if (delta < bestDelta) {
      bestDelta = delta
      best = i
    }
  }

  return best
}

/** X position of the caret at `offset`, or null when there are no carets. */
export function caretXAtOffset(carets: ShapeTextCaret[], offset: number): number | null {
  if (carets.length === 0) return null

  return xOf(carets[clampOffset(carets, offset)]!)
}

/**
 * Start (`direction: -1`) or end (`direction: 1`) of the visual row containing `offset` — the
 * Home/End move. Returns `offset` unchanged when there are no carets to walk.
 */
export function rowBoundaryOffset(
  carets: ShapeTextCaret[],
  offset: number,
  direction: 1 | -1
): number {
  if (carets.length === 0) return offset

  const start = clampOffset(carets, offset)
  const row = rowTopOf(carets[start]!)
  let result = start

  for (let i = start; i >= 0 && i < carets.length; i += direction) {
    if (rowTopOf(carets[i]!) !== row) break
    result = i
  }

  return result
}

/**
 * One visual row up (`-1`) or down (`1`) from `offset`, keeping the caret's x — the Arrow Up/Down
 * move. **Null when this frame has no such row**, which is the signal for the caller to continue
 * into the neighbouring chain member via `rowEdgeOffsetNearestX`.
 */
export function verticalStepOffset(
  carets: ShapeTextCaret[],
  offset: number,
  direction: 1 | -1
): number | null {
  if (carets.length === 0) return null

  const start = clampOffset(carets, offset)
  const rows = rowTops(carets)
  const rowIndex = rows.indexOf(rowTopOf(carets[start]!))

  if (rowIndex < 0) return null

  const targetRow = rows[rowIndex + direction]

  if (targetRow === undefined) return null

  return offsetNearestXInRow(carets, targetRow, xOf(carets[start]!))
}

/**
 * Offset on this frame's first or last visual row nearest to `x` — where a vertical step arriving
 * from an adjacent chain member lands. Falls back to the frame's own extremity when it has no rows.
 */
export function rowEdgeOffsetNearestX(
  carets: ShapeTextCaret[],
  x: number,
  edge: 'first' | 'last'
): number {
  if (carets.length === 0) return 0

  const rows = rowTops(carets)
  const row = edge === 'first' ? rows[0] : rows[rows.length - 1]

  if (row === undefined) return edge === 'first' ? 0 : carets.length - 1

  return offsetNearestXInRow(carets, row, x) ?? (edge === 'first' ? 0 : carets.length - 1)
}

/** Letters, digits and underscore are word characters; everything else separates words. */
function isWordChar(ch: string): boolean {
  return /[\p{L}\p{N}_]/u.test(ch)
}

/**
 * Word-wise caret step — the Ctrl/Alt+Arrow move — in the Windows/Chromium convention QA tests on:
 * forward lands at the START of the next word (consuming the current word then the separators after
 * it), backward lands at the START of the current or previous word.
 *
 * A newline always stops the walk rather than being skipped through, so a word step can never jump
 * a paragraph in one press. It is consumed only when the caret already sits on it, which keeps the
 * step from stalling.
 */
export function wordBoundaryOffset(text: string, offset: number, direction: 1 | -1): number {
  const limit = text.length
  let i = Math.max(0, Math.min(offset, limit))

  if (direction === 1) {
    if (i >= limit) return limit

    if (text[i] === '\n') return i + 1

    while (i < limit && isWordChar(text[i]!)) i++
    while (i < limit && !isWordChar(text[i]!) && text[i] !== '\n') i++

    return i
  }

  if (i <= 0) return 0

  if (text[i - 1] === '\n') return i - 1

  while (i > 0 && !isWordChar(text[i - 1]!) && text[i - 1] !== '\n') i--
  while (i > 0 && isWordChar(text[i - 1]!)) i--

  return i
}

/** Break spaces, i.e. the characters `getShapeTextFlow` is allowed to hang at a row's edge. */
function isBreakSpace(ch: string | undefined): boolean {
  return ch === ' ' || ch === '\t'
}

/**
 * Half-open `[start, end)` of the visual row containing `offset` — the triple-click range.
 *
 * `start` is `rowBoundaryOffset(-1)`. `end` deliberately does **not** delegate to
 * `rowBoundaryOffset(+1)`: that walks `rowTopOf`, i.e. the *continuation* slot, and `advanceSlot`
 * (`shape-text.ts`) annotates the caret at the FIRST character of the new row — so the forward walk
 * stops one caret short of this row's exclusive end and would drop the row's last character. The
 * raw `yTop` is what closes a row, exactly as `computeSelectionRects` closes one from `c0.x`/`c1.x`
 * while opening the next from `c0.nextSlotX`.
 *
 * Trailing break spaces are then trimmed, so a space-driven wrap does not hand back the space it
 * hung at the row's edge. A forced mid-word break has no such space, so its last glyph survives.
 * A `'\n'` needs no trimming: it never goes through `advanceSlot`, so its caret already sits on the
 * next row and the raw walk stops before it.
 *
 * `limit` bounds the walk — pass `layout.consumedLength` so a click on the last laid-out row of an
 * overflowing frame cannot select text nothing draws.
 */
export function visualRowRangeAt(
  carets: ShapeTextCaret[],
  text: string,
  offset: number,
  limit?: number
): ShapeTextRange {
  if (carets.length === 0) return { start: offset, end: offset }

  const { start, end: untrimmedEnd } = untrimmedRowRangeAt(carets, offset, limit)
  let end = untrimmedEnd

  while (end > start && isBreakSpace(text[end - 1])) end--

  return { start, end }
}

/** `visualRowRangeAt` before its trailing break spaces are trimmed. `carets` must not be empty. */
function untrimmedRowRangeAt(
  carets: ShapeTextCaret[],
  offset: number,
  limit?: number
): ShapeTextRange {
  const max = Math.max(0, Math.min(limit ?? carets.length - 1, carets.length - 1))
  const o = Math.min(clampOffset(carets, offset), max)
  const start = rowBoundaryOffset(carets, o, -1)
  const row = rowTopOf(carets[o]!)
  let end = start

  // Raw `yTop`, so the wrap-boundary caret — whose `rowTopOf` already names the row below — still
  // counts as this row's exclusive end.
  while (end + 1 <= max && carets[end + 1]!.yTop === row) end++

  return { start, end }
}

/**
 * Half-open `[start, end)` widened to every visual row it touches: from the start of the row
 * holding `start` to the end of the row holding the last selected character. Leading is a row
 * property — a row's gap to the next one comes from a single run on it (`resolveRowLeadingRun`) —
 * so a Leading change on one word has to reach the whole row, or another run on the row keeps
 * deciding the gap and the change looks like it did nothing (WMPCM-12951).
 *
 * Unlike `visualRowRangeAt`, trailing break spaces are kept: they sit on the row too, and one left
 * at an older Leading would still outrank the new one. Text past `limit` (overflow) has no rows, so
 * a selection reaching into it keeps its own end there, and one wholly inside it is returned as is.
 */
export function visualRowsSpanOf(
  carets: ShapeTextCaret[],
  start: number,
  end: number,
  limit?: number
): ShapeTextRange {
  if (carets.length === 0 || end <= start) return { start, end }
  if (limit !== undefined && start >= limit) return { start, end }

  const first = untrimmedRowRangeAt(carets, start, limit)
  const last = untrimmedRowRangeAt(carets, end - 1, limit)

  return { start: Math.min(first.start, start), end: Math.max(last.end, end) }
}

/**
 * Every whole visual row `[start, end)` touches, one range per row, trailing break spaces kept (see
 * `visualRowsSpanOf`). A `'\n'` between rows belongs to neither. Text past `limit` (overflow) has
 * no rows, so whatever the selection holds there comes back as one range of its own.
 */
export function visualRowRangesOf(
  carets: ShapeTextCaret[],
  start: number,
  end: number,
  limit?: number
): ShapeTextRange[] {
  if (end <= start) return []

  const laidOutEnd = limit === undefined ? end : Math.min(end, limit)
  const rows: ShapeTextRange[] = []
  let offset = start

  while (carets.length > 0 && offset < laidOutEnd) {
    const row = untrimmedRowRangeAt(carets, offset, limit)

    // A row ending where the walk already is means `offset` is the `'\n'` closing it: step over it
    // to the row below.
    if (row.end > offset) rows.push(row)
    offset = Math.max(row.end, offset + 1)
  }

  if (offset < end) rows.push({ start: offset, end })

  return rows
}

/**
 * Half-open `[start, end)` of the token at `offset` — the double-click range.
 *
 * Three classes: word characters (`isWordChar`), break spaces, and everything else (punctuation).
 * `'\n'` belongs to none of them and so bounds every walk for free — a double-click can never
 * select across a paragraph break.
 *
 * A word wins over adjacent whitespace: clicking the boundary right after `hello` selects `hello`,
 * not the space that follows it. That preference is what makes this feel like a browser's own
 * double-click rather than a strict "whatever is at this index" lookup.
 *
 * Deliberately not built from `wordBoundaryOffset`: its forward walk consumes the separators AFTER a
 * word, which is right for the Ctrl+Arrow caret step and wrong for a selection.
 */
export function wordRangeAt(text: string, offset: number): ShapeTextRange {
  const o = Math.max(0, Math.min(offset, text.length))
  const after = text[o]
  const before = o > 0 ? text[o - 1] : undefined

  let pivot: number

  if (after !== undefined && isWordChar(after)) pivot = o
  else if (before !== undefined && isWordChar(before)) pivot = o - 1
  else if (after !== undefined && after !== '\n') pivot = o
  else if (before !== undefined && before !== '\n') pivot = o - 1
  else return { start: o, end: o }

  const sameClass = (ch: string | undefined): boolean => {
    if (ch === undefined || ch === '\n') return false
    const pivotChar = text[pivot]!

    if (isWordChar(pivotChar)) return isWordChar(ch)
    if (isBreakSpace(pivotChar)) return isBreakSpace(ch)

    return !isWordChar(ch) && !isBreakSpace(ch)
  }

  let start = pivot
  let end = pivot + 1

  while (start > 0 && sameClass(text[start - 1])) start--
  while (end < text.length && sameClass(text[end])) end++

  return { start, end }
}

/**
 * Half-open `[start, end)` of the `'\n'`-delimited paragraph containing `offset` — the
 * quadruple-click range. The delimiters themselves are excluded, so typing over the selection cannot
 * silently join two paragraphs.
 *
 * Unlike `visualRowRangeAt` this is NOT bounded by `consumedLength`: a paragraph is a unit of
 * content and the intent is unambiguous, whereas a visual row is a unit of layout and must not reach
 * text the frame does not draw.
 */
export function paragraphRangeAt(text: string, offset: number): ShapeTextRange {
  const o = Math.max(0, Math.min(offset, text.length))
  // `lastIndexOf` clamps a negative `fromIndex` to 0, so without this guard `'\nab'` at offset 0
  // reports a start of 1 — a range that runs backwards.
  const start = o === 0 ? 0 : text.lastIndexOf('\n', o - 1) + 1
  const nextBreak = text.indexOf('\n', o)

  return { start, end: nextBreak === -1 ? text.length : nextBreak }
}

/**
 * Maps a shifted navigation keypress to a direction + granularity, or null when the key is not a
 * selection-extending move.
 *
 * Deliberately per-platform, because the point is to match what the textarea the user is comparing
 * against would have done: on Windows Ctrl+Arrow is word-wise and Home/End are row-wise, while on
 * macOS Alt+Arrow is word-wise, Cmd+Left/Right are row-wise and Cmd+Up/Down jump the whole story.
 * Home/End map to the row on both, since a Mac keyboard that has them is a PC keyboard.
 *
 * Ctrl+Up/Down (Windows) is paragraph-wise natively; it resolves to `line` here rather than growing a
 * paragraph unit for a key no step of WMPCM-9200 asks for — a row step is the conservative subset.
 */
export function resolveShiftNavigation(
  event: ShiftNavigationEvent,
  platform: ShortcutPlatform
): { direction: 1 | -1; unit: ChainSelectionExtendUnit } | null {
  if (!event.shiftKey) return null

  const mac = platform === 'mac'
  const mod = mac ? event.metaKey : event.ctrlKey
  // The non-`mod` modifier on each platform. Anything else held means this is some other shortcut.
  const wordModifier = mac ? event.altKey : event.ctrlKey
  const strayModifier = mac ? event.ctrlKey : event.altKey

  if (strayModifier) return null

  const horizontal = event.key === 'ArrowRight' || event.key === 'ArrowLeft'
  const vertical = event.key === 'ArrowDown' || event.key === 'ArrowUp'
  const homeEnd = event.key === 'End' || event.key === 'Home'

  if (!horizontal && !vertical && !homeEnd) return null

  const direction: 1 | -1 =
    event.key === 'ArrowRight' || event.key === 'ArrowDown' || event.key === 'End' ? 1 : -1

  if (homeEnd) return { direction, unit: mod ? 'documentBoundary' : 'lineBoundary' }

  if (vertical) return { direction, unit: mac && event.metaKey ? 'documentBoundary' : 'line' }

  // Horizontal. On macOS Cmd is row-wise and Alt is word-wise; on Windows only Ctrl is meaningful.
  if (mac && event.metaKey) return { direction, unit: 'lineBoundary' }
  if (wordModifier) return { direction, unit: 'word' }

  return { direction, unit: 'character' }
}
